import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserTable = ({ users, onEditUser, onDeleteUser, onToggleStatus }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedUsers, setSelectedUsers] = useState([]);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'owner', label: 'Owner/Executive' },
    { value: 'chartering', label: 'Chartering Desk' },
    { value: 'finance', label: 'Finance Team' },
    { value: 'compliance', label: 'Compliance Officer' },
    { value: 'operations', label: 'Operations Manager' },
    { value: 'technical', label: 'Technical Superintendent' },
    { value: 'admin', label: 'System Administrator' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const sortedUsers = [...filteredUsers]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aValue === 'string') {
      return aValue?.localeCompare(bValue) * direction;
    }
    return (aValue - bValue) * direction;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers?.length === sortedUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(sortedUsers?.map(user => user?.id));
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-success/10 text-success border-success/20',
      inactive: 'bg-muted text-muted-foreground border-border',
      pending: 'bg-warning/10 text-warning border-warning/20',
      suspended: 'bg-error/10 text-error border-error/20'
    };

    const statusIcons = {
      active: 'CheckCircle',
      inactive: 'Circle',
      pending: 'Clock',
      suspended: 'XCircle'
    };

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${statusStyles?.[status]}`}>
        <Icon name={statusIcons?.[status]} size={12} strokeWidth={2} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      owner: 'Owner/Executive',
      chartering: 'Chartering Desk',
      finance: 'Finance Team',
      compliance: 'Compliance Officer',
      operations: 'Operations Manager',
      technical: 'Technical Superintendent',
      admin: 'System Administrator'
    };
    return roleLabels?.[role] || role;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
      {/* Filters and Search */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <Input
              type="search"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={setRoleFilter}
              placeholder="Filter by role"
              className="w-full sm:w-48"
            />
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              className="w-full sm:w-48"
            />
          </div>
          
          {selectedUsers?.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedUsers?.length} selected
              </span>
              <Button variant="outline" size="sm">
                <Icon name="Mail" size={14} strokeWidth={2} className="mr-2" />
                Send Email
              </Button>
              <Button variant="outline" size="sm">
                <Icon name="Download" size={14} strokeWidth={2} className="mr-2" />
                Export
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers?.length === sortedUsers?.length && sortedUsers?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>User</span>
                  <Icon 
                    name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    strokeWidth={2} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Role</span>
                  <Icon 
                    name={sortField === 'role' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    strokeWidth={2} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Status</span>
                  <Icon 
                    name={sortField === 'status' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    strokeWidth={2} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('lastLogin')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Last Login</span>
                  <Icon 
                    name={sortField === 'lastLogin' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                    strokeWidth={2} 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left">Permissions</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedUsers?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/30 transition-maritime">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => handleSelectUser(user?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-foreground">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{user?.name}</div>
                      <div className="text-sm text-muted-foreground">{user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-foreground">{getRoleLabel(user?.role)}</span>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground">{user?.lastLogin}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {user?.permissions?.slice(0, 3)?.map((permission) => (
                      <span 
                        key={permission}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-accent/10 text-accent"
                      >
                        {permission}
                      </span>
                    ))}
                    {user?.permissions?.length > 3 && (
                      <span className="text-xs text-muted-foreground">
                        +{user?.permissions?.length - 3} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus(user?.id)}
                    >
                      <Icon 
                        name={user?.status === 'active' ? 'UserX' : 'UserCheck'} 
                        size={14} 
                        strokeWidth={2} 
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditUser(user)}
                    >
                      <Icon name="Edit" size={14} strokeWidth={2} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteUser(user?.id)}
                    >
                      <Icon name="Trash2" size={14} strokeWidth={2} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedUsers?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} strokeWidth={1} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">
            {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' ?'Try adjusting your filters or search terms' :'Get started by adding your first user'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default UserTable;